import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * Visual frame shared by every registration step + /weekly-rules + /play +
 * /account. Two stacked backgrounds:
 *
 *   1. Section background — `#463e57 → #31263d` vertical gradient. The
 *      outside-the-card surface in the Figma. (Earlier iteration overlaid
 *      `players.png` here for a stippled noise effect — dropped because
 *      the image didn't stretch cleanly across viewport heights and the
 *      gradient alone matches the latest design comps.)
 *   2. Inner Infoback card — lilac purple gradient with a diagonal-pattern
 *      overlay. The inside-the-card surface.
 *
 * `heading` (e.g. "REGISTER_") sits above the card on steps 4 and 5 per the
 * Figma; steps 1–3 don't have it.
 */
type RegisterLayoutProps = {
  children: ReactNode;
  /** Optional headline rendered above the card (steps 4 + 5 in Figma). */
  heading?: ReactNode;
  /** Optional vertical padding override on the outer section. Defaults match
   *  the rest of the site. */
  outerClassName?: string;
};

export function RegisterLayout({ children, heading, outerClassName }: RegisterLayoutProps) {
  const reduce = useReducedMotion();
  return (
    <section
      className={[
        // `flex-1` grows the section to fill the remaining vertical space of
        // <main>. Without it the section shrinks to its content height and
        // the body background (black) shows through below the card on tall
        // viewports + short steps like /reg-2.
        // Section itself does NOT animate — entrance motion lives on the
        // purple card below. When motion was on the section, the fading
        // section background painted over the footer on mount which looked
        // broken; keeping the section static avoids that overlap.
        'relative isolate flex w-full flex-1 flex-col items-center justify-center overflow-hidden px-[28px] pb-[60px] pt-[60px] sm:px-[40px] sm:pt-[80px] md:px-[64px]',
        outerClassName ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Outer-section background is now owned by <main> in App.tsx — the
          gradient lives there because the section's flex-1 doesn't always
          stretch to fill main, leaving the brand-deep page background
          visible below the card. Mounting on <main> guarantees full-height
          coverage from AppBar to footer with no per-page calc gymnastics. */}

      {/*
        Container max-width 1030px per Figma (Frame 2147238582 on every
        reg screen). The card grows to fill this; individual steps decide
        how wide their inner content should be (forms cap themselves around
        500–720px so inputs/toggles don't stretch the full 1030 on desktop).
      */}
      <div className="relative flex w-full max-w-[1030px] flex-col gap-[20px]">
        {heading && (
          <h1
            className="font-['Orbitron',sans-serif] text-[clamp(36px,8vw,48px)] uppercase leading-[0.95] tracking-[-0.03em] text-white"
            data-test="register-heading"
          >
            {heading}
          </h1>
        )}
        {/*
          Infoback card. Square corners per Figma — the designer intentionally
          left this card sharp (unlike the leaderboard panel which is
          `rounded-tl/tr-[15px]`). Every reg step + WeeklyRules + Play
          renders through this layout, so all the shared corrections
          (1030px max-w, 450px min-h, players.png section bg, vertical-
          centered content) propagate uniformly with no per-step overrides.
        */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 16 }}
          animate={reduce ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative min-h-[450px] overflow-hidden"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #413850 0%, #8c05ea 100%), url('/assets/patterns/diagonal.svg')",
            backgroundBlendMode: 'overlay',
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.16]"
            style={{
              backgroundImage: "url('/assets/patterns/diagonal.svg')",
              backgroundRepeat: 'repeat',
            }}
          />
          {/*
            Inner content area. `justify-center` centers the content
            vertically when the min-h-[450px] card is taller than the
            content; on tall content (consent step) it grows past 450px
            and content lays out from top down.
          */}
          <div className="relative flex min-h-[450px] flex-col items-stretch justify-center gap-[20px] px-[18px] py-[36px] sm:px-[40px] sm:py-[44px]">
            {children}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * The green underscore is used as a Figma typography motif throughout the
 * site (REGISTER_, LEADERBOARD_, etc.). Export a small helper so each step
 * doesn't have to repeat the inline color span.
 */
export function HeadingUnderscore() {
  return <span className="text-[#00ffb2]">_</span>;
}
